import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DatePicker } from 'primeng/datepicker';
import { Subject, debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService, FilingFormState } from '../../../services/mbrs/filing-form-state.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { WarningResult } from '../../../models/validation-warning.model';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { WarningMessageComponent, WarningMessage } from '../../common/warning-message/warning-message.component';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { mapSchemaValidators } from '../../../utils/schema-validators.util';
import { pickRole20SchemaFile } from '../../../services/mbrs/validators/role20-notes-corporate-information.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface SchemaControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    level?: number;
    note?: string;
    rows?: number;
    placeholder?: string;
    options?: string[];
    enumSource?: string;
    conditionalOn?: string;
    conditionalValue?: string;
  };
  error?: {
    required?: string;
    requiredCrossSheet?: string;
    pattern?: string;
    min?: string;
    max?: string;
    [key: string]: string | undefined;
  };
  requiredIfCrossSheet?: {
    sheet: string;
    field: string;
    equals: string;
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: SchemaControl[];
}

@Component({
  selector: 'app-role20-710000-notes-corporate-information',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputText,
    ButtonModule,
    Toast,
    DatePicker,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    WarningMessageComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe
  ],
  providers: [MessageService],
  templateUrl: './role20-710000-notes-corporate-information.component.html',
  styleUrls: ['./role20-710000-notes-corporate-information.component.css']
})
export class Role20710000NotesCorporateInformationComponent implements OnInit, OnDestroy {
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  form!: FormGroup;
  schema!: FormSchema;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Notes - Corporate Information (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.notesCorporateInformation
    );
  }
  isLoading = true;
  private isLoadingState = false;

  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint blanks a date the user is half-way through typing.
   * Mirrors role23/role24, which carry the identical listener and guard.
   */
  private isInitialLoadComplete = false;

  // Track setTimeout handles so they can be cancelled on destroy
  // Prevents late state-service writes when the component is destroyed during background validation
  private initTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '20',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      await this.loadSchema();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupCrossReferenceAutoPopulation();
      this.setupCrossSheetValidation();
      this.setupWarningEvaluation();
      this.evaluateFieldWarnings();
      this.runInitialValidation();
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Cancel pending init timeouts to prevent late state-service writes
    // after the component is destroyed during background validation cycle
    for (const id of this.initTimeoutIds) {
      clearTimeout(id);
    }
    this.initTimeoutIds = [];
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.notesCorporateInformation),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((notesCorporateInformationData) => {
        if (notesCorporateInformationData && Object.keys(notesCorporateInformationData).length > 0) {
          // After initial load completes, skip re-patching. `state$` is the
          // MODIFIED stream, so this sheet's own debounced persist comes
          // straight back here — and `patchValue` reaches PrimeNG's
          // `writeValue`, which re-renders the input from the model even under
          // `emitEvent: false`. While a date is half-typed that model is null
          // (`DatePicker.onUserInput` calls `updateModel(null)` on text its
          // `parseValueFromString` rejects), so the echo blanks the digits the
          // user just entered. Adopting a SIBLING TAB's values is
          // `adoptRemoteState()`'s job, on `remoteUpdateApplied$`.
          // Mirrors role12/role23/role24. See isInitialLoadComplete.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Convert date string to Date object for PrimeNG DatePicker
          const processedData = { ...notesCorporateInformationData };
          if (processedData['ssmt:DateOfIncorporation']) {
            processedData['ssmt:DateOfIncorporation'] = new Date(
              processedData['ssmt:DateOfIncorporation']
            );
          }

          // Patch the form with the new data
          this.form.patchValue(processedData, { emitEvent: false });

          // NOTE: Cross-reference auto-population logic already checks if fields are empty
          // So backend data will NOT be overwritten by cross-reference data
          // The setupCrossReferenceAutoPopulation() method checks isEmpty at line 285

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * Load schema from JSON file (dynamically selects MFRS or MPERS)
   */
  private async loadSchema(): Promise<void> {
    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role20');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Get the accounting standard from filing info
    const filingData = this.stateService.getActiveFilingInfoData();
    const accountingStandard = filingData['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';

    // Determine which schema file to load (MFRS default, MPERS when the
    // accounting-standard literal matches). Selection logic is shared
    // with role20NotesCorporateInformationValidator via pickRole20SchemaFile
    // so both paths cannot drift apart.
    const schemaFile = pickRole20SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      `/assets/forms/${schemaFile}`,
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    const formControls: any = {};

    if (this.schema && this.schema.controls) {
      this.schema.controls.forEach(control => {
        if (control.ui.control !== 'section-header') {
          const defaultValue = this.getDefaultValue(control);
          const validators = this.getValidators(control);
          formControls[control.qname] = new FormControl(defaultValue, validators);
        }
      });
    }

    this.form = new FormGroup(formControls);
  }

  /**
   * Get default value for control based on type
   */
  private getDefaultValue(control: SchemaControl): any {
    switch (control.type) {
      case 'YesNoItemType':
        return null;
      case 'DateItemType':
        return null;
      case 'NonNegativeIntegerItemType':
        return null;
      case 'TextBlock':
      case 'StringItemType':
      default:
        return '';
    }
  }

  /**
   * Get validators for control
   */
  private getValidators(control: SchemaControl): any[] {
    const validators = [];

    // Check if field is required from schema
    const hasRequiredValidator = (control as any).validators?.some((validator: any) => validator.name === 'required');
    const isRequired = (control as any).required === true || hasRequiredValidator;

    if (isRequired) {
      validators.push(Validators.required);
    }

    // Numeric/format constraints (min/max/pattern) from the schema, derived per
    // control via the shared util. NonNegativeInteger* types resolve to min(0)
    // inside the util — see schema-validators.util.
    validators.push(...mapSchemaValidators(control));

    return validators;
  }

  /**
   * Check if field should be displayed based on conditional logic
   */
  shouldDisplayField(control: SchemaControl): boolean {
    if (!control.ui.conditionalOn) {
      return true;
    }

    const parentControl = this.form?.get(control.ui.conditionalOn);
    if (!parentControl) {
      return true;
    }

    return parentControl.value === control.ui.conditionalValue;
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.notesCorporateInformation);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.notesCorporateInformation);

    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });
    }

    // Small delay to ensure form is fully initialized before enabling auto-save
    this.initTimeoutIds.push(setTimeout(() => {
      this.isLoadingState = false;

      // Snapshot baseline after all debounced valueChanges settle
      this.initTimeoutIds.push(setTimeout(() => {
        // Re-baseline the transformed form value (esp. the rich-text field's on-load
        // fragment normalization) so the snapshot equals what the auto-save persist
        // writes; without it a full revert never cleared the pencil.
        this.stateService.updateSheetData(
          SHEET_KEYS.notesCorporateInformation,
          this.form.value,
          this.form.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
        this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.notesCorporateInformation, !this.form.valid);
        // Initial load has settled — from here the state listener stops re-patching.
        this.isInitialLoadComplete = true;
      }, 600));
    }, 100));
  }

  /**
   * Setup automatic state persistence
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        this.stateService.updateSheetData(SHEET_KEYS.notesCorporateInformation, this.form.value, this.form.valid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.notesCorporateInformation, { hasErrors: !this.form.valid });
      },
    });
  }

  /**
   * Setup cross-reference auto-population from Role-1
   * Auto-populates company name, registration number, country of incorporation
   */
  private setupCrossReferenceAutoPopulation(): void {
    setTimeout(() => {
      // Only auto-populate if fields are empty
      const currentData = this.stateService.getSheetData(SHEET_KEYS.notesCorporateInformation);
      const isEmpty = !currentData || Object.keys(currentData).length === 0;

      if (isEmpty) {
        // Auto-populate company name from Role-1
        const companyName = this.crossRefService.getCrossReferenceData().companyName;
        if (companyName && this.form.get('ssmt:NameOfReportingEntity')) {
          this.form.patchValue({
            'ssmt:NameOfReportingEntity': companyName
          }, { emitEvent: false });
        }

        // Auto-populate registration number from Role-1
        const regNumber = this.crossRefService.getCrossReferenceData().registrationNumber;
        if (regNumber && this.form.get('ssmt:RegistrationNumber')) {
          this.form.patchValue({
            'ssmt:RegistrationNumber': regNumber
          }, { emitEvent: false });
        }

        // Auto-populate country of incorporation from Role-1
        const country = this.crossRefService.getCrossReferenceData().originOfCompany;
        if (country && this.form.get('ssmt:CountryOfIncorporation')) {
          // Extract country from "Incorporated in Malaysia" format
          const countryName = country.includes('Malaysia') ? 'Malaysia' : country;
          this.form.patchValue({
            'ssmt:CountryOfIncorporation': countryName,
            'ssmt:DomicileOfEntity': countryName
          }, { emitEvent: false });
        }
      }

      // Subscribe to ongoing changes in Role-1 (reactive updates)
      this.crossRefService.getCompanyName$()
        .pipe(takeUntil(this.destroy$))
        .subscribe(companyName => {
          if (companyName && this.form.get('ssmt:NameOfReportingEntity')) {
            const currentValue = this.form.get('ssmt:NameOfReportingEntity')?.value;
            // Only update if field is empty to avoid overwriting user edits
            if (!currentValue) {
              this.form.patchValue({
                'ssmt:NameOfReportingEntity': companyName
              }, { emitEvent: false });
            }
          }
        });

      this.crossRefService.getRegistrationNumber$()
        .pipe(takeUntil(this.destroy$))
        .subscribe(regNumber => {
          if (regNumber && this.form.get('ssmt:RegistrationNumber')) {
            const currentValue = this.form.get('ssmt:RegistrationNumber')?.value;
            if (!currentValue) {
              this.form.patchValue({
                'ssmt:RegistrationNumber': regNumber
              }, { emitEvent: false });
            }
          }
        });
    }, 400);
  }

  /**
   * Setup cross-sheet validation for fields that depend on other sheets
   * Listens to scopeOfFiling sheet and dynamically updates validators
   */
  private setupCrossSheetValidation(): void {
    // Find controls with cross-sheet requirements
    const crossSheetControls = this.schema.controls.filter(
      control => control.requiredIfCrossSheet
    );

    if (crossSheetControls.length === 0) {
      return;
    }

    // Listen to state changes for cross-sheet validation
    this.stateService.state$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesCorporateInformation)),
        map(state => state.scopeOfFiling),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Re-arm cross-sheet required validators when the upstream sheet changes.
        // Same method runs at init (runInitialValidation) so the two paths cannot
        // drift — see applyCrossSheetRequired().
        this.applyCrossSheetRequired();
        this.form.updateValueAndValidity({ emitEvent: false });
      });

    // ----------------------------------
    // Cross-sheet local re-validation watch — also covers filingPrerequisites
    // (role-0 cascade rule). evaluateFieldWarnings() reads filingPrerequisites
    // @ L194, so any change must trigger re-evaluation of the cached
    // fieldWarnings even when no scopeOfFiling field changed.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesCorporateInformation)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
        })),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab safety net — sibling-tab edits to scopeOfFiling or
    // filingPrerequisites must trigger re-evaluation of cross-sheet
    // validators and warnings here too.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesCorporateInformation)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey }) => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role20-notesCorporateInformation',
          //   sourceSheetKey: sheetKey,
          // });
        }
        this.evaluateFieldWarnings();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // notesCorporateInformation slice, `applyRemoteChange` rehydrates the
    // shared state and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges).
    //
    // Deliberately a SEPARATE subscription from the cross-tab safety net
    // above, but that safety net does NOT filter on `changedSheets` — it has
    // no slice filter at all — so it fires on EVERY remote `modifiedState`
    // update, including one that changed THIS sheet's own slice. It is
    // registered first, so on an own-slice remote update it runs
    // `evaluateFieldWarnings()` once against the still-STALE, pre-adoption
    // form, then this subscription runs `adoptRemoteState()`. The end state
    // is correct; the intermediate evaluation between the two is
    // wrong-valued for one microtask. Deliberately NOT deduped — deduping
    // risks dropping the upstream-driven refresh the safety net exists for
    // (scopeOfFiling/filingPrerequisites slice changes that never touch
    // `changedSheets` for this sheet at all); see fix-wave I4.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.notesCorporateInformation)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesCorporateInformation)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role20-notesCorporateInformation',
          // });
        }
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.form, this.stateService.getSheetData(SHEET_KEYS.notesCorporateInformation));
    this.evaluateFieldWarnings();
  }

  /**
   * Auto-fill demo data
   */
  autoFillDemo(): void {
    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;
    const currentYearStart = new Date(previousYear, 0, 1);
    const currentYearEnd = new Date(previousYear, 11, 31);

    const demoData: any = {
      'ssmt:DisclosureOfLegalFormOfEntity': 'Private company limited by shares incorporated under the Companies Act 2016 in Malaysia',
      'ssmt:DomicileOfEntity': 'Malaysia',
      'ssmt:CountryOfIncorporation': 'Malaysia',
      'ssmt:DateOfIncorporation': new Date(2010, 5, 15), // June 15, 2010
      'ssmt:RegistrationNumber': '202001012345 (1234567-X)',
      'ssmt:AddressOfRegisteredOfficeOfEntity': 'No. 123, Jalan Industri,\nTaman Perindustrian,\n47000 Petaling Jaya,\nSelangor Darul Ehsan',
      'ssmt:AddressOfPrincipalPlaceOfBusiness': 'No. 456, Jalan Kilang,\nKawasan Perindustrian,\n40000 Shah Alam,\nSelangor Darul Ehsan',
      'ssmt:DescriptionOfNatureOfEntitysOperations': 'The Company is principally engaged in the manufacturing and trading of industrial equipment and machinery. The Company operates manufacturing facilities in Selangor and distribution centers across Peninsular Malaysia.',
      'ssmt:DescriptionOfPrincipalActivities': 'The principal activities of the Company during the financial year consisted of:\n\n(a) Manufacturing of precision industrial machinery and equipment\n(b) Trading and distribution of imported industrial parts and components\n(c) Provision of maintenance and technical support services for industrial equipment\n\nThere have been no significant changes in the nature of these activities during the financial year.',
      'ssmt:DisclosureOfWhetherThereHaveBeenAnySignificantChangesInNatureOfPrincipalActivities': 'No',
      'ssmt:DisclosureOfWhetherEntityHasParentEntity': 'Yes',
      'ssmt:NameOfParentEntity': 'ABC Holdings Sdn Bhd',
      'ssmt:CountryOfIncorporationOfParentEntity': 'Malaysia',
      'ssmt:NameOfUltimateParentOfGroup': 'Global Industrial Group Ltd',
      'ssmt:CountryOfIncorporationOfUltimateParentEntity': 'Singapore',
      'ssmt:DisclosureOfBoardOfDirectors': 'The directors who served during the financial year and up to the date of this report are:\n\nTan Sri Dato\' Ahmad bin Abdullah (Chairman)\nDatin Siti binti Hassan (Managing Director)\nEncik Lim Chong Wei (Executive Director)\nPuan Nurul Huda binti Ismail (Independent Non-Executive Director)\nMr. John Smith (Independent Non-Executive Director)',
      'ssmt:DisclosureOfWhetherEntityIsHoldingCompany': 'No',
      'ssmt:NameOfAuditorsOfEntity': 'Audit Excellence PLT',
      'ssmt:AuditFirmRegistrationNumber': 'AF 1598'
    };

    this.form.patchValue(demoData);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: 'Corporate information form has been filled with sample data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    if (confirm('Are you sure you want to clear all data in this form?')) {
      this.form.reset();
      this.stateService.clearSheetData(SHEET_KEYS.notesCorporateInformation);

      this.messageService.add({
        severity: 'info',
        summary: 'Form Cleared',
        detail: 'All form data has been cleared',
        life: TOAST_DURATION_DEFAULT_MS
      });
    }
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
        life: TOAST_DURATION_DEFAULT_MS
      });
      return;
    }

    const formData = this.form.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Corporate information has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.form.get(qname);
  }

  /**
   * Check if a field is required
   */
  isFieldRequired(control: SchemaControl): boolean {
    return isControlMandatory(control as any, {
      selfData: this.form?.getRawValue() ?? {},
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as keyof FilingFormState) as any)?.[field],
    });
  }

  /**
   * Run initial validation on page load to show errors immediately
   */
  /**
   * Arm/clear `Validators.required` for every `requiredIfCrossSheet` control from
   * the CURRENT upstream value, and mark a now-invalid control as touched so its
   * inline error renders immediately.
   *
   * Single source of truth for cross-sheet required-state: called BOTH at init
   * (runInitialValidation) and from the scopeOfFiling change subscription
   * (setupCrossSheetValidation). The init call is the fix for the
   * "no inline error on entry" bug — previously the only arming path lived inside
   * the subscription, whose initial emission is suppressed by the
   * `isSheetInitializing` filter, so a field that was already conditionally
   * required on entry never got its validator armed (only the pure validator,
   * which drives the dropdown verdict, flagged it). Idempotent and emitEvent-free
   * so it never marks the sheet as edited.
   */
  private applyCrossSheetRequired(): void {
    if (!this.schema?.controls || !this.form) {
      return;
    }
    for (const control of this.schema.controls) {
      if (!control.requiredIfCrossSheet) {
        continue;
      }
      const { sheet, field, equals } = control.requiredIfCrossSheet;
      const refSheetData = this.stateService.getSheetData(sheet as keyof FilingFormState);
      const refFieldValue = refSheetData?.[field];

      const formControl = this.form.get(control.qname);
      if (!formControl) {
        continue;
      }

      if (refFieldValue === equals) {
        formControl.setValidators([Validators.required]);
      } else {
        formControl.clearValidators();
      }
      formControl.updateValueAndValidity({ emitEvent: false });

      // Mark as touched if now invalid, so the error displays immediately.
      if (formControl.invalid) {
        formControl.markAsTouched();
      }
    }
  }

  private runInitialValidation(): void {
    // Arm cross-sheet required validators from the current upstream values BEFORE
    // marking touched, so a field that is already conditionally-required-and-empty
    // on entry shows its inline error immediately (not only after the upstream
    // value changes while this sheet is mounted).
    this.applyCrossSheetRequired();

    // Reveal the sheet's already-computed errors. This sheet's arming step above
    // is what genuinely changes validity, and it stays here rather than moving
    // into the shared util precisely because of that — role 20 is the only sheet
    // with `requiredIfCrossSheet` controls. The reveal itself is touch-only.
    revealValidationErrors(this.form, {
      isComputed: computedControlPredicate(this.schema?.controls),
    });
  }

  /**
   * Get error message for a control and error type
   * Uses custom error message from schema if available, otherwise falls back to default
   */
  getErrorMessage(control: SchemaControl, errorType: string): string {
    // Check for cross-sheet requirement error first
    if (errorType === 'required' && control.requiredIfCrossSheet) {
      const { sheet, field, equals } = control.requiredIfCrossSheet;
      const refSheetData = this.stateService.getSheetData(sheet as keyof FilingFormState);
      const refFieldValue = refSheetData?.[field];

      // If the cross-sheet condition is met, use the custom cross-sheet error message
      if (refFieldValue === equals && control.error?.requiredCrossSheet) {
        return control.error.requiredCrossSheet;
      }
    }

    // Check if control has custom error messages defined in schema
    if (control.error && control.error[errorType]) {
      return control.error[errorType] as string;
    }

    // Fall back to default error messages
    switch (errorType) {
      case 'required':
        return `${control.label} is required`;
      case 'pattern':
        return `Invalid format. ${control.ui.placeholder || ''}`;
      case 'min':
        return `Value must be greater than or equal to 0`;
      case 'max':
        return `Value exceeds maximum allowed`;
      default:
        return 'Invalid value';
    }
  }

  /**
   * Setup warning evaluation for all fields with warnings defined in schema
   */
  private setupWarningEvaluation(): void {
    if (!this.schema?.controls) return;

    // Re-evaluate warnings whenever form values change
    this.form.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });
  }

  /**
   * Evaluate field warnings for all fields with warning rules
   * Called on initial load and on form value changes
   */
  private evaluateFieldWarnings(): void {
    if (!this.schema || !this.form) return;

    // Get all form values for cross-field validations
    const allFormValues = this.form.getRawValue();

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control: SchemaControl) => {
      if ((control as any).warnings && (control as any).warnings.length > 0) {
        const formControl = this.form.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            (control as any).warnings,
            allFormValues,
            true // Always evaluate warnings
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          } else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });

  }

  /**
   * Get warnings for a specific field
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.form.markAsPristine();
  }
}
